import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

// Import contexts and util modules
import {
  getProcess,
  isBookingProcessAlias,
  ITEM,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import { ensureTransaction } from '../../util/data';
import { isDigitalProductListingType } from '../../util/soulwise-marketplace/listingType';
import { listingHasOnlineMode } from '../../util/soulwise-marketplace/listingFields';
import {
  isPriceTierConflictError,
  isPriceTierSoldOutError,
  isPriceTierUnavailableError,
  isTransactionInitiateListingNotFoundError,
} from '../../util/errors';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { pathByRouteName } from '../../util/routes';
import { DATE_TYPE_DATE, DATE_TYPE_DATETIME, LINE_ITEM_HOUR, propTypes } from '../../util/types';
import { createSlug } from '../../util/urlHelpers';

// Import shared components
import { H2, NamedLink, OrderBreakdown, Page } from '../../components';

import {
  bookingDatesMaybe,
  getBillingDetails,
  getFormattedTotalPrice,
  getShippingDetailsMaybe,
  getTransactionTypeData,
  pageDataForCheckoutSpeculate,
  processCheckoutWithoutPayment,
  recoverCheckoutAfterPriceTierChange,
  setOrderPageInitialValues,
  shouldShowCheckoutConfirm,
  shouldShowCheckoutPaymentForm,
  speculatedQuoteMatchesSelectedTier,
  checkoutQuoteForDisplay,
  checkoutConfirmHasQuotedTotal,
} from './CheckoutPageTransactionHelpers.js';
import { getErrorMessages } from './ErrorMessages';

import CustomTopbar from './CustomTopbar';
import DetailsSideCard from './DetailsSideCard';
import MobileListingImage from './MobileListingImage';
import MobileOrderBreakdown from './MobileOrderBreakdown';
import SimpleOrderForm from './StripePaymentForm/SimpleOrderForm';
import CheckoutPackageRecap from './CheckoutPackageRecap';
import CheckoutTierRecap from './CheckoutTierRecap';
import { storeData } from './CheckoutPageSessionHelpers';
import { useCheckoutPriceTierRace } from './useCheckoutPriceTierRace';

import {
  getItemEventTrackingParams,
  sendTrackingEvent,
  TRACKING_EVENTS,
} from '../../analytics/trackingEvent';
import { ListingOverview } from '../../components/ListingOverview/ListingOverview';
import css from './CheckoutPage.module.css';

/**
 * Construct orderParams object using pageData from session storage, shipping details, and optional payment params.
 * Note: This is used for both speculate transition and real transition
 *       - Speculate transition is called, when the the component is mounted. It's used to test if the data can go through the API validation
 *       - Real transition is made, when the user submits the OrderForm.
 *
 * @param {Object} pageData data that's saved to session storage.
 * @param {Object} shippingDetails shipping address if applicable.
 * @param {Object} config app-wide configs. This contains hosted configs too.
 * @returns orderParams.
 */
const getOrderParams = (pageData, shippingDetails, config) => {
  const { listingType, unitType } = pageData?.listing?.attributes?.publicData || {};
  const hasBookingDates = !!pageData.orderData?.bookingDates?.bookingStart;
  const quantityFromOrder = pageData.orderData?.quantity;
  const isDigitalListing = isDigitalProductListingType(listingType);
  const quantity =
    isDigitalListing || (unitType === ITEM && !hasBookingDates)
      ? quantityFromOrder != null && quantityFromOrder !== ''
        ? quantityFromOrder
        : 1
      : quantityFromOrder;
  const quantityMaybe = quantity != null && quantity !== '' ? { quantity } : {};
  const deliveryMethod = pageData.orderData?.deliveryMethod;
  const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};
  const tierId = pageData.orderData?.tierId;
  const tierIdMaybe = tierId ? { tierId } : {};

  const listingId = pageData?.listing?.id?.uuid;
  const bookingStart = pageData.orderData?.bookingDates?.bookingStart?.toISOString();
  const bookingEnd = pageData.orderData?.bookingDates?.bookingEnd?.toISOString();
  const protectedBookingDatesMaybe = bookingStart && bookingEnd ? { bookingStart, bookingEnd } : {};

  const protectedDataMaybe = {
    protectedData: {
      listingId,
      ...protectedBookingDatesMaybe,
      ...getTransactionTypeData(listingType, unitType, config),
      ...deliveryMethodMaybe,
      ...shippingDetails,
    },
  };

  // These are the order parameters for the first payment-related transition
  // which is either initiate-transition or initiate-transition-after-enquiry
  const orderParams = {
    listingId: pageData?.listing?.id,
    ...deliveryMethodMaybe,
    ...quantityMaybe,
    ...bookingDatesMaybe(pageData.orderData?.bookingDates),
    ...tierIdMaybe,
    ...protectedDataMaybe,
  };
  return orderParams;
};

const fetchSpeculatedTransactionIfNeeded = (orderParams, pageData, fetchSpeculatedTransaction) => {
  const pageDataListing = pageData.listing;
  const processName =
    pageData?.transaction?.attributes?.processName ||
    pageDataListing?.attributes?.publicData?.transactionProcessAlias?.split('/')[0];
  const process = processName ? getProcess(resolveLatestProcessName(processName)) : null;
  const speculatePageData = pageDataForCheckoutSpeculate({
    pageData,
    process,
    isClockInSync: true,
  });
  const tx = speculatePageData ? speculatePageData.transaction : null;

  // If transaction has passed payment-pending state, speculated tx is not needed.
  const shouldFetchSpeculatedTransaction =
    !!pageData?.listing?.id && !!pageData.orderData && !!process;

  if (shouldFetchSpeculatedTransaction) {
    const processAlias = pageData.listing.attributes.publicData?.transactionProcessAlias;
    const transactionId = tx ? tx.id : null;
    const isInquiryInPaymentProcess =
      tx?.attributes?.lastTransition === process.transitions.INQUIRE;

    const requestTransition = isInquiryInPaymentProcess
      ? process.transitions.REQUEST_BOOKING_AFTER_INQUIRY
      : process.transitions.REQUEST_BOOKING;
    const isPrivileged = process.isPrivileged(requestTransition);

    fetchSpeculatedTransaction(
      orderParams,
      processAlias,
      transactionId,
      requestTransition,
      isPrivileged
    );
  }
};

/**
 * Load initial data for the page
 *
 * Since the data for the checkout is not passed in the URL (there
 * might be lots of options in the future), we must pass in the data
 * some other way. Currently the ListingPage sets the initial data
 * for the CheckoutPage's Redux store.
 *
 * For some cases (e.g. a refresh in the CheckoutPage), the Redux
 * store is empty. To handle that case, we store the received data
 * to window.sessionStorage and read it from there if no props from
 * the store exist.
 *
 * This function also sets of fetching the speculative transaction
 * based on this initial data.
 */
export const loadInitialData = ({ pageData, fetchSpeculatedTransaction, config }) => {
  // Fetch speculated transaction for showing price in order breakdown
  // NOTE: if unit type is line-item/item, quantity needs to be added.
  // The way to pass it to checkout page is through pageData.orderData
  const shippingDetails = {};
  const orderParams = getOrderParams(pageData, shippingDetails, config);

  fetchSpeculatedTransactionIfNeeded(orderParams, pageData, fetchSpeculatedTransaction);
};

const handleSubmit = (values, process, props, submitting, setSubmitting) => {
  if (submitting) {
    return;
  }
  setSubmitting(true);

  const {
    history,
    config,
    intl,
    routeConfiguration,
    speculatedTransaction,
    currentUser,
    dispatch,
    onInitiateOrder,
    onSendMessage,
    onSubmitCallback,
    pageData,
    setPageData,
    sessionStorageKey,
  } = props;
  const { message, formValues } = values;

  const requestPaymentParams = {
    pageData,
    speculatedTransaction,
    billingDetails: getBillingDetails(formValues),
    message,
    process,
    onInitiateOrder,
    onSendMessage,
    sessionStorageKey,
    setPageData,
  };

  const shippingDetails = getShippingDetailsMaybe(formValues);

  // These are the order parameters for the first payment-related transition
  // which is either initiate-transition or initiate-transition-after-enquiry
  const orderParams = getOrderParams(pageData, shippingDetails, config);

  // There are multiple XHR calls that needs to be made against the Sharetribe Marketplace API on checkout with payments
  processCheckoutWithoutPayment(orderParams, requestPaymentParams)
    .then(response => {
      const { orderId, messageSuccess } = response;
      setSubmitting(false);

      const initialMessageFailedToTransaction = messageSuccess ? null : orderId;
      const orderDetailsPath = pathByRouteName('OrderDetailsPage', routeConfiguration, {
        id: orderId.uuid,
      });
      const initialValues = {
        initialMessageFailedToTransaction,
      };

      setOrderPageInitialValues(initialValues, routeConfiguration, dispatch);
      onSubmitCallback();
      const itemParams = getItemEventTrackingParams(pageData?.listing, config, intl);
      sendTrackingEvent({
        name: TRACKING_EVENTS.PURCHASE,
        parameters: {
          transaction_id: orderId.uuid,
          currency: itemParams.currency,
          value: itemParams.value,
          items: [itemParams],
        },
      });
      history.push(orderDetailsPath);
    })
    .catch(err => {
      console.error(err);
      setSubmitting(false);
    });
};

export const CheckoutPageWithoutPayment = props => {
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  // Initialized stripe library is saved to state - if it's needed at some point here too.

  const {
    scrollingDisabled,
    speculateTransactionError,
    speculatedTransaction: speculatedTransactionMaybe,
    initiateOrderError,
    intl,
    currentUser,
    pageData,
    processName,
    listingTitle,
    title,
    config,
  } = props;

  // Since the listing data is already given from the ListingPage
  // and stored to handle refreshes, it might not have the possible
  // deleted or closed information in it. If the transaction
  // initiate or the speculative initiate fail due to the listing
  // being deleted or closed, we should dig the information from the
  // errors and not the listing data.
  const listingNotFound =
    isTransactionInitiateListingNotFoundError(speculateTransactionError) ||
    isTransactionInitiateListingNotFoundError(initiateOrderError);

  const { listing, transaction, orderData } = pageData;
  const { setPageData, fetchSpeculatedTransaction, sessionStorageKey } = props;
  const { onClearCheckoutOrderErrors, onRefreshCheckoutListing } = props;
  const {
    soldOutTierIds,
    unitsRefreshNonce,
    hasEligiblePriceTier,
    handleTierAvailabilityChange,
    refreshListingAndUnits,
    soldOutNoticeActive,
    hidePriceTierGoBack,
    hideStaleSoldOutQuote,
    acknowledgeSoldOutNotice,
    holdAutoPick,
    hasPriceTiers,
  } = useCheckoutPriceTierRace({
    listing,
    orderData,
    initiateOrderError,
    speculateTransactionError,
    onRefreshCheckoutListing,
    setPageData,
    pageData,
  });

  const handleSelectPriceTier = tierId => {
    acknowledgeSoldOutNotice();
    if (!setPageData) {
      return;
    }
    const recovery = recoverCheckoutAfterPriceTierChange({ pageData, tierId });
    if (!recovery.changed) {
      return;
    }
    if (recovery.shouldClearErrors && onClearCheckoutOrderErrors) {
      onClearCheckoutOrderErrors();
    }
    const nextPageData = recovery.pageData;
    setPageData(nextPageData);
    storeData(nextPageData.orderData, listing, nextPageData.transaction, sessionStorageKey);
    refreshListingAndUnits();
    const orderParams = getOrderParams(nextPageData, {}, config);
    fetchSpeculatedTransactionIfNeeded(orderParams, nextPageData, fetchSpeculatedTransaction);
  };
  const existingTransaction = ensureTransaction(transaction);
  const speculatedTransaction = ensureTransaction(speculatedTransactionMaybe, {}, null);
  const quoteMatchesSelectedTier = speculatedQuoteMatchesSelectedTier({
    speculatedTransaction: speculatedTransactionMaybe,
    selectedTierId: orderData?.tierId,
  });

  // If existing transaction has line-items, it has gone through one of the request-payment transitions.
  // Otherwise, we try to rely on speculatedTransaction for order breakdown data.
  const tx =
    existingTransaction?.attributes?.lineItems?.length > 0 && quoteMatchesSelectedTier
      ? existingTransaction
      : quoteMatchesSelectedTier
      ? speculatedTransaction
      : {};
  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  const unitType = listing?.attributes?.publicData?.unitType;
  const lineItemUnitType = `line-item/${unitType}`;
  const dateType = lineItemUnitType === LINE_ITEM_HOUR ? DATE_TYPE_DATETIME : DATE_TYPE_DATE;
  const txBookingMaybe = tx?.booking?.id ? { booking: tx.booking, dateType, timeZone } : {};
  const listingType = listing?.attributes?.publicData?.listingType;
  const showTimezoneSelector = !isDigitalProductListingType(listingType);

  // Show breakdown only when (speculated?) transaction is loaded
  // (i.e. it has an id and lineItems)
  const breakdown = tx.id ? (
    <OrderBreakdown
      className={css.orderBreakdown}
      userRole="customer"
      transaction={tx}
      processName={processName}
      {...txBookingMaybe}
      currency={config.currency}
      marketplaceName={config.marketplaceName}
      showTimezoneSelector={showTimezoneSelector}
      listing={listing}
    />
  ) : null;

  const rawTotalPrice =
    tx?.attributes?.lineItems?.length > 0 ? getFormattedTotalPrice(tx, intl) : null;
  const { breakdown: displayBreakdown, totalPrice } = checkoutQuoteForDisplay({
    hideStaleSoldOutQuote,
    breakdown,
    totalPrice: rawTotalPrice,
  });

  const process = processName ? getProcess(resolveLatestProcessName(processName)) : null;
  const transitions = process.transitions;

  const showPaymentForm = shouldShowCheckoutPaymentForm({
    currentUser,
    listingNotFound,
    initiateOrderError,
    speculateTransactionError,
  });
  const showConfirmPayment = shouldShowCheckoutConfirm({
    showPaymentForm,
    quoteMatchesSelectedTier,
    initiateOrderError,
    speculateTransactionError,
    hasEligiblePriceTier: !hasPriceTiers || hasEligiblePriceTier,
    hasQuotedTotal: checkoutConfirmHasQuotedTotal({ hasPriceTiers, totalPrice }),
    soldOutNoticeActive,
  });

  const firstImage = listing?.images?.length > 0 ? listing.images[0] : null;

  const listingLink = (
    <NamedLink
      name="ListingPage"
      params={{ id: listing?.id?.uuid, slug: createSlug(listingTitle) }}
    >
      <FormattedMessage id="CheckoutPage.errorlistingLinkText" />
    </NamedLink>
  );

  const hideConflict = error =>
    hidePriceTierGoBack && (isPriceTierSoldOutError(error) || isPriceTierUnavailableError(error));
  const errorMessages = getErrorMessages(
    listingNotFound,
    hideConflict(initiateOrderError) ? null : initiateOrderError,
    null,
    null,
    hideConflict(speculateTransactionError) ? null : speculateTransactionError,
    listingLink
  );

  const txTransitions = existingTransaction?.attributes?.transitions || [];
  const hasInquireTransition = txTransitions.find(tr => tr.transition === transitions.INQUIRE);
  const showInitialMessageInput = !hasInquireTransition;

  // Get first and last name of the current user and use it in the StripePaymentForm to autofill the name field
  const userName = currentUser?.attributes?.profile
    ? `${currentUser.attributes.profile.firstName} ${currentUser.attributes.profile.lastName}`
    : null;

  // If your marketplace works mostly in one country you can use initial values to select country automatically
  // e.g. {country: 'FI'}

  const initalValuesForStripePayment = {
    name: userName,
    recipientName: userName,
  };
  const askShippingDetails = orderData?.deliveryMethod === 'shipping';

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <CustomTopbar intl={intl} />
      <div className={css.contentContainer}>
        <MobileListingImage
          listingTitle={listingTitle}
          author={listing?.author}
          firstImage={firstImage}
          layoutListingImageConfig={config.layout.listingImage}
        />
        <div className={css.orderFormContainer}>
          <div className={css.headingContainer}>
            <H2 as="h1" className={css.heading}>
              {title}
            </H2>
            <ListingOverview
              listing={listing}
              author={listing?.author}
              location={location}
              rootClassName={css.ListingOverview}
            />
          </div>

          <MobileOrderBreakdown
            speculateTransactionErrorMessage={errorMessages.speculateTransactionErrorMessage}
            breakdown={hasPriceTiers ? null : displayBreakdown}
          />

          <section className={css.paymentContainer}>
            {errorMessages.initiateOrderErrorMessage}
            {errorMessages.listingNotFoundErrorMessage}
            {errorMessages.speculateErrorMessage}
            {errorMessages.paymentExpiredMessage}
            {soldOutNoticeActive ? (
              <p className={css.orderError} data-testid="price-tier-sold-out-choose-another">
                <FormattedMessage id="CheckoutPage.priceTierSoldOutChooseAnother" />
              </p>
            ) : null}

            <CheckoutPackageRecap listing={listing} intl={intl} config={config} />
            {hasPriceTiers ? (
              <CheckoutTierRecap
                listing={listing}
                orderData={orderData}
                intl={intl}
                onSelectTier={handleSelectPriceTier}
                onAvailabilityChange={handleTierAvailabilityChange}
                holdAutoPick={holdAutoPick}
                soldOutTierIds={soldOutTierIds}
                unitsRefreshKey={[
                  orderData?.tierId || '',
                  unitsRefreshNonce,
                  isPriceTierConflictError(initiateOrderError) ||
                  isPriceTierConflictError(speculateTransactionError)
                    ? 'sold-out'
                    : 'available',
                ].join(':')}
              />
            ) : null}
            {hasPriceTiers ? (
              <div className={css.subscriptionBreakdownMobile}>{displayBreakdown}</div>
            ) : null}

            {showPaymentForm ? (
              <SimpleOrderForm
                className={css.paymentForm}
                onSubmit={values => handleSubmit(values, process, props, submitting, setSubmitting)}
                inProgress={submitting}
                formId="CheckoutPagePaymentForm"
                authorDisplayName={listing?.author?.attributes?.profile?.displayName}
                showInitialMessageInput={showInitialMessageInput}
                initialValues={initalValuesForStripePayment}
                initiateOrderError={
                  isPriceTierConflictError(initiateOrderError) ? null : initiateOrderError
                }
                showSubmitButton={showConfirmPayment}
                askShippingDetails={askShippingDetails}
                showPickUplocation={orderData?.deliveryMethod === 'pickup'}
                listingLocation={listing?.attributes?.publicData?.location}
                totalPrice={totalPrice}
                locale={config.localization.locale}
                marketplaceName={config.marketplaceName}
                isBooking={isBookingProcessAlias(transactionProcessAlias)}
                isFuzzyLocation={config.maps.fuzzy.enabled}
                isOnlineListing={listingHasOnlineMode(listing)}
                processName={processName}
              />
            ) : null}
          </section>
        </div>

        <DetailsSideCard
          listing={listing}
          listingTitle={listingTitle}
          author={listing?.author}
          firstImage={firstImage}
          layoutListingImageConfig={config.layout.listingImage}
          speculateTransactionErrorMessage={errorMessages.speculateTransactionErrorMessage}
          isInquiryProcess={false}
          processName={processName}
          breakdown={displayBreakdown}
          intl={intl}
        />
      </div>
    </Page>
  );
};

CheckoutPageWithoutPayment.defaultProps = {
  initiateOrderError: null,
  confirmPaymentError: null,
  listing: null,
  orderData: {},
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  currentUser: null,
  onClearCheckoutOrderErrors: null,
  onRefreshCheckoutListing: null,
};

CheckoutPageWithoutPayment.propTypes = {
  scrollingDisabled: bool.isRequired,
  listing: propTypes.listing,
  orderData: object,
  fetchSpeculatedTransaction: func.isRequired,
  speculateTransactionInProgress: bool.isRequired,
  speculateTransactionError: propTypes.error,
  speculatedTransaction: propTypes.transaction,
  transaction: propTypes.transaction,
  currentUser: propTypes.currentUser,
  params: shape({
    id: string,
    slug: string,
  }).isRequired,
  onInitiateOrder: func.isRequired,
  onSendMessage: func.isRequired,
  initiateOrderError: propTypes.error,
  onClearCheckoutOrderErrors: func,
  onRefreshCheckoutListing: func,

  // from connect
  dispatch: func.isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
};

export default CheckoutPageWithoutPayment;
